/**
 * The pending-question inbox and the popup that announces a new question
 * (prompt.md §23, §78).
 *
 * WHY a list plus a non-modal popup instead of a modal dialog: the popup has to
 * announce a question that arrived while the user was reading something else,
 * without stealing focus or pretending the question is urgent enough to block the
 * app. It carries the same answer form the conversation does, so it can be
 * answered in place, and it disappears the moment the durable request does —
 * which is what makes answering on another device close it here.
 */

import type { ReactNode } from "react";

import { Z_INDEX } from "@dayvise/design-tokens";

import { Badge, EmptyState, Spinner } from "../primitives/Feedback.tsx";
import { Button } from "../primitives/Button.tsx";
import { Card } from "../primitives/Surfaces.tsx";
import { MUTED_TEXT, cardStyle, rowStyle, stackStyle } from "../styles.ts";
import {
  answerableCount,
  inboxHeadline,
  notificationPermissionLabel,
  type InboxRequestView,
  type NotificationPermissionStatus,
} from "../logic/inputInbox.ts";

function stateTone(state: InboxRequestView["state"]): "neutral" | "warning" | "danger" {
  if (state === "expired") return "danger";
  if (state === "expiring") return "warning";
  return "neutral";
}

function stateLabel(state: InboxRequestView["state"]): string {
  if (state === "expired") return "Expired";
  if (state === "expiring") return "Expiring soon";
  return "Waiting";
}

function NotificationControl({
  status,
  busy,
  onEnable,
}: {
  readonly status: NotificationPermissionStatus;
  readonly busy: boolean;
  readonly onEnable?: (() => void) | undefined;
}) {
  return (
    <div style={stackStyle("var(--dayvise-space-xxs)")}>
      <span style={MUTED_TEXT}>{notificationPermissionLabel(status)}</span>
      {status === "default" && onEnable ? (
        <div style={rowStyle("var(--dayvise-space-xs)")}>
          <Button size="sm" variant="ghost" loading={busy} onClick={onEnable}>
            Turn on desktop notifications
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export interface InputRequestRowProps {
  readonly view: InboxRequestView;
  readonly selected?: boolean;
  readonly onSelect?: ((requestId: string) => void) | undefined;
  readonly onOpenConversation?: ((conversationId: string) => void) | undefined;
}

/** One pending question, selectable so its answer form can be shown. */
export function InputRequestRow({ view, selected = false, onSelect, onOpenConversation }: InputRequestRowProps) {
  return (
    <div
      style={{
        ...stackStyle("var(--dayvise-space-xs)"),
        padding: "var(--dayvise-space-sm)",
        borderRadius: "var(--dayvise-radius-md)",
        border: `1px solid ${selected ? "var(--dayvise-color-accent)" : "var(--dayvise-color-border)"}`,
        background: "var(--dayvise-color-surface)",
      }}
    >
      <div style={rowStyle("var(--dayvise-space-xs)", "space-between")}>
        <strong style={{ fontSize: "var(--dayvise-font-size-sm)" }}>{view.title}</strong>
        <span style={rowStyle("var(--dayvise-space-xs)")}>
          <Badge tone={stateTone(view.state)}>{stateLabel(view.state)}</Badge>
          {view.credentialFieldCount > 0 ? <Badge tone="accent">{view.credentialFieldCount} protected</Badge> : null}
        </span>
      </div>
      <span style={MUTED_TEXT}>{view.detail}</span>
      {view.remainingLabel === null ? null : <span style={MUTED_TEXT}>{view.remainingLabel}</span>}
      <div style={rowStyle("var(--dayvise-space-xs)")}>
        {onSelect ? (
          <Button size="sm" variant={selected ? "primary" : "secondary"} disabled={!view.answerable} onClick={() => onSelect(view.requestId)}>
            {view.answerable ? (selected ? "Answering now" : "Answer this question") : "Expired — cannot be answered"}
          </Button>
        ) : null}
        {onOpenConversation ? (
          <Button size="sm" variant="ghost" onClick={() => onOpenConversation(view.conversationId)}>
            Open the conversation
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export interface InputRequestListProps {
  readonly views: readonly InboxRequestView[];
  /** The request whose form is currently open, if any. */
  readonly selectedId?: string | null;
  readonly loading?: boolean;
  readonly error?: string | null;
  readonly onSelect?: ((requestId: string) => void) | undefined;
  readonly onRefresh?: (() => void) | undefined;
  readonly onOpenConversation?: ((conversationId: string) => void) | undefined;
  readonly notificationStatus?: NotificationPermissionStatus;
  readonly notificationsBusy?: boolean;
  readonly onEnableNotifications?: (() => void) | undefined;
  /** The answer form for the selected request; rendered below the list. */
  readonly children?: ReactNode;
}

export function InputRequestList({
  views,
  selectedId = null,
  loading = false,
  error = null,
  onSelect,
  onRefresh,
  onOpenConversation,
  notificationStatus = "unsupported",
  notificationsBusy = false,
  onEnableNotifications,
  children,
}: InputRequestListProps) {
  const pending = answerableCount(views);
  return (
    <Card
      title="Questions waiting for you"
      subtitle="A run that asked for input is paused, not duplicated: answering here resumes the same run, and answering on another device closes this one."
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        <div style={rowStyle("var(--dayvise-space-xs)", "space-between")}>
          <span role="status">{inboxHeadline(views)}</span>
          <span style={rowStyle("var(--dayvise-space-xs)")}>
            {pending > 0 ? <Badge tone="accent">{pending} to answer</Badge> : null}
            {onRefresh ? (
              <Button size="sm" variant="ghost" loading={loading} onClick={onRefresh}>
                Refresh
              </Button>
            ) : null}
          </span>
        </div>
        <NotificationControl status={notificationStatus} busy={notificationsBusy} onEnable={onEnableNotifications} />
        {error === null ? null : <p role="alert" style={{ color: "var(--dayvise-color-danger)", margin: 0 }}>{error}</p>}
        {loading && views.length === 0 ? <Spinner label="Loading questions" /> : null}
        {!loading && views.length === 0 ? (
          <EmptyState title="No questions are pending" description="When a run needs an answer, it appears here and in the in-app popup." />
        ) : null}
        {views.map((view) => (
          <InputRequestRow
            key={view.requestId}
            view={view}
            selected={view.requestId === selectedId}
            onSelect={onSelect}
            onOpenConversation={onOpenConversation}
          />
        ))}
        {children}
      </div>
    </Card>
  );
}

export interface InputRequestPopupProps {
  readonly view: InboxRequestView;
  /** How many questions are answerable right now, so the popup is not one of many surprises. */
  readonly pendingCount: number;
  readonly onDismiss: () => void;
  readonly onOpenConversation?: ((conversationId: string) => void) | undefined;
  readonly notificationStatus?: NotificationPermissionStatus;
  readonly notificationsBusy?: boolean;
  readonly onEnableNotifications?: (() => void) | undefined;
  /** The answer form, rendered once the user opens the popup to answer in place. */
  readonly children?: ReactNode;
  readonly busy?: boolean;
}

export function InputRequestPopup({
  view,
  pendingCount,
  onDismiss,
  onOpenConversation,
  notificationStatus = "unsupported",
  notificationsBusy = false,
  onEnableNotifications,
  children,
  busy = false,
}: InputRequestPopupProps) {
  return (
    <aside
      role="region"
      aria-label="Pending agent question"
      style={{
        ...cardStyle({ elevated: true }),
        position: "fixed",
        right: "var(--dayvise-space-sm)",
        bottom: "var(--dayvise-space-sm)",
        width: "min(380px, calc(100vw - var(--dayvise-space-md)))",
        maxHeight: "70vh",
        overflowY: "auto",
        zIndex: Z_INDEX.toast,
      }}
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        <div style={rowStyle("var(--dayvise-space-xs)", "space-between")}>
          <strong style={{ fontSize: "var(--dayvise-font-size-sm)" }}>{view.title}</strong>
          <Badge tone={stateTone(view.state)}>{stateLabel(view.state)}</Badge>
        </div>
        <span style={MUTED_TEXT}>
          {view.detail}
          {pendingCount > 1 ? ` · ${pendingCount - 1} more waiting in the inbox` : ""}
        </span>
        {view.remainingLabel === null ? null : <span style={MUTED_TEXT}>{view.remainingLabel}</span>}
        {children ?? null}
        <div style={rowStyle("var(--dayvise-space-xs)", "space-between")}>
          <span style={rowStyle("var(--dayvise-space-xs)")}>
            {onOpenConversation ? (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => onOpenConversation(view.conversationId)}>
                Open the conversation
              </Button>
            ) : null}
          </span>
          <Button size="sm" variant="ghost" disabled={busy} onClick={onDismiss}>
            Later
          </Button>
        </div>
        <NotificationControl status={notificationStatus} busy={notificationsBusy} onEnable={onEnableNotifications} />
      </div>
    </aside>
  );
}
